/**
 * PayerNameSheet — "Who's paying?" bottom sheet for anonymous dine-in
 * diners (spec §4/§7).
 *
 * Shown when DineInOrderScreen / SplitPayScreen mount with NO usable
 * identity (no customer token, no group participant, no stored payer token
 * for this order). One name field + confirm CTA, mirroring GroupStartSheet.
 * On confirm it mints a payer-session against the QR table's active order,
 * persists the token (lib/payer-token), and fires onEstablished so the
 * parent re-fetches with the new identity.
 *
 * Error states:
 *  - 404 → the table has no active order (friendly "ask your waiter" copy).
 *  - missing table context (deep link without QR) → prompt to scan the QR.
 */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "@/components/group/BottomSheet";
import { ApiError } from "@/api/http";
import { payerSessionApi } from "@/api/payer-session";
import { deviceFingerprint } from "@/lib/participant-token";
import { writePayer } from "@/lib/payer-token";
import { useTable } from "@/store/table";

export interface PayerNameSheetProps {
  open: boolean;
  /** The order the caller wants to pay against (route param). */
  orderId: string;
  /** Fired after the payer token is stored — parent re-fetches. */
  onEstablished: () => void;
  /** Fired when the diner dismisses the sheet without identifying. */
  onCancel: () => void;
}

export function PayerNameSheet({
  open,
  orderId,
  onEstablished,
  onCancel,
}: PayerNameSheetProps) {
  const { t } = useTranslation();
  const { tableInfo } = useTable();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const branchId = tableInfo?.branchId ?? null;
  const tableId = tableInfo?.tableId ?? null;
  const missingTable = !branchId || !tableId;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || submitting || missingTable) return;
    setSubmitting(true);
    setError(null);
    try {
      const session = await payerSessionApi.create(branchId!, tableId!, {
        displayName: name.trim(),
        deviceFingerprint: deviceFingerprint(),
      });
      // The session is scoped to the table's CURRENT active order. If that
      // isn't the order on this route (stale link after the table turned
      // over), storing it is still correct — the follow-up fetch will 403
      // on the stale order and the diner lands on the live one.
      writePayer({
        payerToken: session.payerToken,
        guestId: session.guestId,
        orderId: session.orderId,
        displayName: name.trim(),
      });
      onEstablished();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError(t("v2.payer.no_active_order"));
      } else {
        setError(t("v2.payer.generic_error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={() => {
        if (!submitting) onCancel();
      }}
      ariaLabel={t("v2.payer.whos_paying")}
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            className="display"
            style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)" }}
          >
            {t("v2.payer.whos_paying")}
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>
            {t("v2.payer.whos_paying_sub")}
          </div>
        </div>

        {missingTable ? (
          <div
            role="alert"
            style={{
              fontSize: 13,
              color: "var(--ink-2)",
              background: "var(--surface-2)",
              padding: "12px 14px",
              borderRadius: "var(--radius-card)",
              textAlign: "center",
            }}
          >
            {t("v2.payer.no_table_context")}
          </div>
        ) : (
          <label
            style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-2)",
                letterSpacing: 0.2,
              }}
            >
              {t("v2.payer.your_name")}
            </span>
            <input
              data-testid="payer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("v2.payer.your_name_placeholder")}
              maxLength={32}
              autoFocus
              style={{
                height: 44,
                padding: "0 14px",
                borderRadius: "var(--radius-card)",
                border: "1px solid var(--border)",
                background: "var(--surface-1)",
                fontSize: 15,
                outline: "none",
              }}
            />
          </label>
        )}

        {error && (
          <div
            role="alert"
            style={{
              fontSize: 12,
              color: "#b91c1c",
              background: "#fee2e2",
              padding: "8px 12px",
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}

        {!missingTable && (
          <button
            data-testid="payer-name-continue"
            type="submit"
            disabled={submitting || !name.trim()}
            style={{
              width: "100%",
              height: 48,
              borderRadius: 12,
              background:
                submitting || !name.trim()
                  ? "var(--surface-2-strong, #e5e7eb)"
                  : "var(--primary-600)",
              color: "#fff",
              border: "none",
              fontSize: 15,
              fontWeight: 700,
              cursor: submitting ? "wait" : "pointer",
            }}
          >
            {submitting ? "…" : t("v2.payer.continue")}
          </button>
        )}
      </form>
    </BottomSheet>
  );
}
